import { Locale } from "@redotech/crdb-enums/common/locale";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { getLocaleName } from "@redotech/redo-i18n/locales";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { alertMessage, toast } from "@redotech/redo-web/alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  flowStepTranslationsQueryKey,
  generateLanguageTranslations,
  languageRemovalDeletes,
  removeLanguageTranslations,
  selectedTranslationLanguages,
  stepElementIdsOf,
  StepTranslationElement,
  stepTranslationEntries,
  TRANSLATIONS_STALE_TIME,
} from "./step-translations";
import { staleTranslationLanguages } from "./translation-staleness";
import { useFlowTextTranslationsEnabled } from "./use-flow-text-translations-enabled";

export function useStepTranslationLanguages({
  elements,
  flowType,
}: {
  elements: StepTranslationElement[];
  flowType: FlowType;
}) {
  const enabled = useFlowTextTranslationsEnabled();
  const { t, i18n } = useTranslation();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  const [languageToRemove, setLanguageToRemove] = useState<Locale>();
  const elementIds = useMemo(() => stepElementIdsOf(elements), [elements]);
  const query = useQuery({
    queryKey: flowStepTranslationsQueryKey(elementIds),
    queryFn: async () => {
      const { translations } = await rpcClient.getFlowElementTranslations({
        elementIds,
      });
      return translations;
    },
    enabled: enabled && elementIds.length > 0,
    staleTime: TRANSLATIONS_STALE_TIME,
  });
  const selected = useMemo(
    () => selectedTranslationLanguages(query.data ?? [], i18n.language),
    [query.data, i18n.language],
  );
  const staleLanguages = useMemo(
    () =>
      staleTranslationLanguages({ elements, translations: query.data ?? [] }),
    [elements, query.data],
  );

  const addLanguageMutation = useMutation({
    mutationFn: (language: Locale) =>
      generateLanguageTranslations({
        client: rpcClient,
        elements,
        language,
        flowType,
      }),
    onSuccess: async ({ untranslated }, language) => {
      if (untranslated.length > 0) {
        toast(
          t(
            'Some fields could not be translated to {{language}}. Open "View translations" to add them manually.',
            { language: getLocaleName(language, i18n.language) },
          ),
          { variant: "warning" },
        );
      }
      await queryClient.invalidateQueries({
        queryKey: flowStepTranslationsQueryKey(elementIds),
      });
    },
    onError: (_error, language) =>
      alertMessage(
        t(
          "Generating {{language}} translations failed. The language was not added. Please try again.",
          { language: getLocaleName(language, i18n.language) },
        ),
      ),
  });
  const removeLanguageMutation = useMutation({
    mutationFn: (language: Locale) =>
      removeLanguageTranslations({
        client: rpcClient,
        translations: query.data ?? [],
        language,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: flowStepTranslationsQueryKey(elementIds),
      });
      setLanguageToRemove(undefined);
    },
    onError: (_error, language) => {
      setLanguageToRemove(undefined);
      alertMessage(
        t("Removing {{language}} translations failed. Please try again.", {
          language: getLocaleName(language, i18n.language),
        }),
      );
    },
  });
  const pendingLanguage = addLanguageMutation.isPending
    ? addLanguageMutation.variables
    : undefined;

  const addLanguage = useHandler((language: Locale) => {
    if (!stepTranslationEntries(elements).length) {
      alertMessage(t("Add text to this step before generating translations."));
      return;
    }
    addLanguageMutation.mutate(language);
  });
  const requestRemoval = useHandler((language: Locale) =>
    setLanguageToRemove(language),
  );
  const confirmRemoval = useHandler(() => {
    if (languageToRemove) {
      removeLanguageMutation.mutate(languageToRemove);
    }
  });
  const cancelRemoval = useHandler(() => setLanguageToRemove(undefined));
  const removalCount = languageToRemove
    ? languageRemovalDeletes(query.data ?? [], languageToRemove).length
    : 0;

  return {
    enabled,
    elementIds,
    loadFailed: !!query.error,
    loading: query.isPending || addLanguageMutation.isPending,
    mutating: addLanguageMutation.isPending || removeLanguageMutation.isPending,
    selected,
    pendingLanguage,
    staleLanguages,
    addLanguage,
    requestRemoval,
    languageToRemove,
    removalCount,
    removalPending: removeLanguageMutation.isPending,
    confirmRemoval,
    cancelRemoval,
  };
}
